import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const ApiComparisonTool = () => {
  const [regularApiResponse, setRegularApiResponse] = useState(null);
  const [diagnosticApiResponse, setDiagnosticApiResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [differences, setDifferences] = useState([]);

  useEffect(() => {
    fetchBothEndpoints();
  }, []);

  const fetchBothEndpoints = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
      
      // Fetch from both endpoints
      const regularResponse = await axios.get(`${apiUrl}/api/bookings`, { headers });
      const diagnosticResponse = await axios.get(`${apiUrl}/api/diagnostic/bookings`, { headers });
      
      console.log('Regular API response:', regularResponse.data);
      console.log('Diagnostic API response:', diagnosticResponse.data);
      
      setRegularApiResponse(regularResponse.data);
      setDiagnosticApiResponse(diagnosticResponse.data);
      
      // Compare the responses
      compareResponses(regularResponse.data, diagnosticResponse.data);
    } catch (error) {
      console.error('Error fetching API data:', error);
      setError('Error fetching API data: ' + error.message);
      toast.error(`Failed to load API data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const compareResponses = (regularData, diagnosticData) => {
    const differences = [];
    
    // Check if both have success property
    if (regularData.success !== diagnosticData.success) {
      differences.push({
        property: 'success',
        regularValue: regularData.success,
        diagnosticValue: diagnosticData.success
      });
    }
    
    // Check data structure
    const regularHasData = !!regularData.data;
    const diagnosticHasData = !!diagnosticData.data;
    
    if (regularHasData !== diagnosticHasData) {
      differences.push({
        property: 'data property',
        regularValue: regularHasData ? 'present' : 'missing',
        diagnosticValue: diagnosticHasData ? 'present' : 'missing'
      });
    }
    
    // Check if regular API has pagination
    const regularHasPagination = regularData.data && 
                                regularData.data.current_page !== undefined && 
                                regularData.data.data !== undefined;
    
    if (regularHasPagination) {
      differences.push({
        property: 'pagination',
        regularValue: 'paginated response',
        diagnosticValue: 'direct array'
      });
      
      // Compare booking counts
      const regularCount = regularData.data.data ? regularData.data.data.length : 0;
      const diagnosticCount = Array.isArray(diagnosticData.data) ? diagnosticData.data.length : 0;
      
      if (regularCount !== diagnosticCount) {
        differences.push({
          property: 'booking count',
          regularValue: regularCount,
          diagnosticValue: diagnosticCount
        });
      }
    } else {
      // Direct comparison of arrays
      const regularArray = Array.isArray(regularData.data) ? regularData.data : 
                          (regularData.data && Array.isArray(regularData.data.data)) ? regularData.data.data : [];
      const diagnosticArray = Array.isArray(diagnosticData.data) ? diagnosticData.data : [];
      
      if (regularArray.length !== diagnosticArray.length) {
        differences.push({
          property: 'booking count',
          regularValue: regularArray.length,
          diagnosticValue: diagnosticArray.length
        });
      }
    }
    
    setDifferences(differences);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">API Endpoint Comparison Tool</h1>
        <button
          onClick={fetchBothEndpoints}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Refresh
        </button>
      </div>
      
      {loading ? (
        <div className="animate-pulse p-6">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
        </div>
      ) : error ? (
        <div className="p-6 text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={fetchBothEndpoints}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Differences Table */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">API Response Differences</h2>
            
            {differences.length === 0 ? (
              <p className="text-green-600">No structural differences found between the APIs.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Property
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Regular API (/api/bookings)
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Diagnostic API (/api/diagnostic/bookings)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {differences.map((diff, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {diff.property}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {JSON.stringify(diff.regularValue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {JSON.stringify(diff.diagnosticValue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Response Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Regular API Response */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Regular API Response</h2>
              <div className="bg-gray-100 p-4 rounded-md overflow-auto text-xs max-h-96">
                <pre>{JSON.stringify(regularApiResponse, null, 2)}</pre>
              </div>
            </div>
            
            {/* Diagnostic API Response */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Diagnostic API Response</h2>
              <div className="bg-gray-100 p-4 rounded-md overflow-auto text-xs max-h-96">
                <pre>{JSON.stringify(diagnosticApiResponse, null, 2)}</pre>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApiComparisonTool;
